"use client";

import { useState } from "react";
import {
  DataTable,
  Notice,
  type Option,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  ExampleButton,
  NoticeList,
  StatementCard,
} from "@/components/tools/publishing-statements/kit";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  assembleSummary,
  findJargon,
  type LengthTarget,
  LONG_SENTENCE_WORDS,
  lengthStatus,
  readability,
  type SummaryLayout,
  TARGET_AVERAGE_SENTENCE,
} from "@/lib/tool-kit/publishing/plain-language";
import type { RichParagraph } from "@/lib/tool-kit/publishing/rich-text";
import {
  countWords,
  findAcronyms,
} from "@/lib/tool-kit/publishing/text-checks";

/** English strings. A translated content file overrides these through `ui`. */
export const PLAIN_LANGUAGE_SUMMARY_GENERATOR_STRINGS = {
  problemLabel: "1. The problem — why did you do this study?",
  problemPlaceholder: "Many people… We wanted to find out whether…",
  methodsLabel: "2. What you did",
  methodsPlaceholder: "We asked… We measured…",
  findingsLabel: "3. What you found",
  findingsPlaceholder: "We found that…",
  importanceLabel: "4. Why it matters",
  importancePlaceholder: "This matters because… Next, we want to…",
  words: "one: word|other: words",
  targetLabel: "Target length",
  targetBrief: "Short — up to 100 words",
  targetPlos: "150–200 words (PLOS author summary)",
  targetAgu: "Up to 200 words (AGU journals)",
  targetCochrane: "400–850 words (Cochrane review)",
  targetCustom: "Custom",
  customMinLabel: "Minimum words",
  customMaxLabel: "Maximum words",
  layoutLabel: "Layout",
  layoutParagraph: "One paragraph",
  layoutHeaded: "With headings",
  headingProblem: "Why we did this study",
  headingMethods: "What we did",
  headingFindings: "What we found",
  headingImportance: "What this means",
  tryExample: "Try an example",
  exampleProblem:
    "Many people over 65 sleep badly. Sleeping pills can help, but they also make falls more likely. We wanted to know whether a daily walk could improve sleep instead.",
  exampleMethods:
    "We randomized 240 participants aged 65 to 80 into two groups. One group walked for 30 minutes every afternoon for 12 weeks. The other group kept to their usual routine. Everyone wore a wrist sensor that recorded their sleep.",
  exampleFindings:
    "People in the walking group fell asleep about 15 minutes faster and slept about 30 minutes longer each night than people in the other group. No one was hurt while walking.",
  exampleImportance:
    "A daily walk is cheap, safe and easy to start. Doctors could suggest it before prescribing sleeping pills. Next, we want to find out whether the benefits last after the 12 weeks end.",
  results: "Your summary",
  summaryHeading: "Plain language summary",
  copy: "Copy summary",
  copied: "Copied!",
  prompt: "Answer at least one of the four prompts to start your summary.",
  targetRange: "{min}–{max} words",
  targetMax: "up to {max} words",
  statusWithin: "Within your target ({target}).",
  statusUnder:
    "one: {count} word below your target ({target}).|other: {count} words below your target ({target}).",
  statusOver:
    "one: {count} word over your target ({target}).|other: {count} words over your target ({target}).",
  checkHeading: "Readability check",
  statWords: "Words",
  statSentences: "Sentences",
  statAverage: "Average sentence length",
  statGrade: "Flesch–Kincaid grade",
  gradeEasy: "Grade {grade}: plain enough for most adult readers.",
  gradeMiddle: "Grade {grade}: high-school level — simplify where you can.",
  gradeHard:
    "Grade {grade}: college level — hard going for a general audience.",
  averageAdvice:
    "Your sentences average more than {target} words. Cochrane's guidance for plain language summaries suggests about {target} — split the longest ones.",
  jargonHeading: "Jargon to reconsider",
  jargonFound: "In your text",
  jargonPlain: "Plainer alternative",
  jargonCount: "Times",
  jargonNone: "No words from the jargon list were found.",
  acronymNotice: "Spell out or replace these abbreviations: {list}.",
  longHeading: "Long sentences ({count} words or more)",
  longItem: "one: {count} word — {text}|other: {count} words — {text}",
  listSeparator: ", ",
} as const;

type Strings = typeof PLAIN_LANGUAGE_SUMMARY_GENERATOR_STRINGS;
type TargetId = "brief" | "plos" | "agu" | "cochrane" | "custom";
type SectionId = "problem" | "methods" | "findings" | "importance";

const SECTION_IDS: readonly SectionId[] = [
  "problem",
  "methods",
  "findings",
  "importance",
];

const TARGETS: Record<Exclude<TargetId, "custom">, LengthTarget> = {
  brief: { min: 0, max: 100 },
  plos: { min: 150, max: 200 },
  agu: { min: 0, max: 200 },
  cochrane: { min: 400, max: 850 },
};

const EMPTY: Record<SectionId, string> = {
  problem: "",
  methods: "",
  findings: "",
  importance: "",
};

function sectionStrings(id: SectionId, strings: Strings) {
  const table = {
    problem: [
      strings.problemLabel,
      strings.problemPlaceholder,
      strings.headingProblem,
    ],
    methods: [
      strings.methodsLabel,
      strings.methodsPlaceholder,
      strings.headingMethods,
    ],
    findings: [
      strings.findingsLabel,
      strings.findingsPlaceholder,
      strings.headingFindings,
    ],
    importance: [
      strings.importanceLabel,
      strings.importancePlaceholder,
      strings.headingImportance,
    ],
  } as const;
  const [label, placeholder, heading] = table[id];
  return { label, placeholder, heading };
}

function gradeText(grade: number, display: string, strings: Strings): string {
  if (grade <= 8) {
    return t(strings.gradeEasy, { grade: display });
  }
  return grade <= 12
    ? t(strings.gradeMiddle, { grade: display })
    : t(strings.gradeHard, { grade: display });
}

export default function PlainLanguageSummaryGeneratorWidget() {
  const strings = useToolUi(PLAIN_LANGUAGE_SUMMARY_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [answers, setAnswers] = useState<Record<SectionId, string>>(EMPTY);
  const [targetId, setTargetId] = useState<TargetId>("plos");
  const [customMin, setCustomMin] = useState("100");
  const [customMax, setCustomMax] = useState("250");
  const [layout, setLayout] = useState<SummaryLayout>("paragraph");

  const sections = SECTION_IDS.map((id) => ({
    heading: sectionStrings(id, strings).heading,
    text: answers[id],
  }));
  const summary = assembleSummary(sections, "paragraph");
  const stats = readability(summary);
  const wordCount = countWords(summary);
  const target: LengthTarget =
    targetId === "custom"
      ? {
          min: Math.max(parseIntegerInput(customMin) ?? 0, 0),
          max: Math.max(parseIntegerInput(customMax) ?? 0, 1),
        }
      : TARGETS[targetId];
  const targetText =
    target.min > 0
      ? t(strings.targetRange, { min: fmt(target.min), max: fmt(target.max) })
      : t(strings.targetMax, { max: fmt(target.max) });
  const status = lengthStatus(wordCount, target);
  const statusText = {
    within: t(strings.statusWithin, { target: targetText }),
    under: tPlural(strings.statusUnder, locale, target.min - wordCount, {
      count: fmt(target.min - wordCount),
      target: targetText,
    }).trim(),
    over: tPlural(strings.statusOver, locale, wordCount - target.max, {
      count: fmt(wordCount - target.max),
      target: targetText,
    }).trim(),
  }[status];

  const paragraphs: RichParagraph[] =
    layout === "paragraph"
      ? [[{ text: summary }]]
      : sections
          .filter((section) => section.text.trim() !== "")
          .flatMap((section): RichParagraph[] => [
            [{ text: section.heading, bold: true }],
            [{ text: assembleSummary([section], "paragraph") }],
          ]);
  const jargon = findJargon(summary);
  const acronyms = findAcronyms(summary);
  const notes: string[] = [statusText];
  if (stats && stats.averageSentenceLength > TARGET_AVERAGE_SENTENCE) {
    notes.push(
      t(strings.averageAdvice, { target: fmt(TARGET_AVERAGE_SENTENCE) })
    );
  }
  if (acronyms.length > 0) {
    notes.push(
      t(strings.acronymNotice, { list: acronyms.join(strings.listSeparator) })
    );
  }

  const targetOptions: Option<TargetId>[] = [
    { value: "brief", label: strings.targetBrief },
    { value: "plos", label: strings.targetPlos },
    { value: "agu", label: strings.targetAgu },
    { value: "cochrane", label: strings.targetCochrane },
    { value: "custom", label: strings.targetCustom },
  ];
  const oneDecimal = { minimumFractionDigits: 1, maximumFractionDigits: 1 };

  return (
    <div className="space-y-6">
      {SECTION_IDS.map((id) => {
        const text = sectionStrings(id, strings);
        const words = countWords(answers[id]);
        return (
          <TextAreaField
            hint={`${fmt(words)}${tPlural(strings.words, locale, words)}`}
            id={`pls-${id}`}
            key={id}
            label={text.label}
            onChange={(value) =>
              setAnswers((current) => ({ ...current, [id]: value }))
            }
            placeholder={text.placeholder}
            prose
            rows={3}
            value={answers[id]}
          />
        );
      })}
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField<TargetId>
          id="pls-target"
          label={strings.targetLabel}
          onChange={setTargetId}
          options={targetOptions}
          value={targetId}
        />
        <PillGroup<SummaryLayout>
          label={strings.layoutLabel}
          name="pls-layout"
          onChange={setLayout}
          options={[
            { value: "paragraph", label: strings.layoutParagraph },
            { value: "headed", label: strings.layoutHeaded },
          ]}
          value={layout}
        />
        {targetId === "custom" ? (
          <>
            <TextField
              id="pls-min"
              label={strings.customMinLabel}
              numeric
              onChange={setCustomMin}
              value={customMin}
            />
            <TextField
              id="pls-max"
              label={strings.customMaxLabel}
              numeric
              onChange={setCustomMax}
              value={customMax}
            />
          </>
        ) : null}
      </div>
      <ExampleButton
        label={strings.tryExample}
        onClick={() =>
          setAnswers({
            problem: strings.exampleProblem,
            methods: strings.exampleMethods,
            findings: strings.exampleFindings,
            importance: strings.exampleImportance,
          })
        }
      />

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {stats ? (
          <>
            <StatementCard
              copiedLabel={strings.copied}
              copyLabel={strings.copy}
              heading={strings.summaryHeading}
              paragraphs={paragraphs}
            />
            <NoticeList items={notes} />
            <h3 className={WIDGET_H3}>{strings.checkHeading}</h3>
            <StatGrid>
              <StatCard label={strings.statWords} value={fmt(stats.words)} />
              <StatCard
                label={strings.statSentences}
                value={fmt(stats.sentences)}
              />
              <StatCard
                label={strings.statAverage}
                value={fmt(stats.averageSentenceLength, oneDecimal)}
              />
              <StatCard
                emphasis
                label={strings.statGrade}
                value={fmt(stats.grade, oneDecimal)}
              />
            </StatGrid>
            <p className={HINT}>
              {gradeText(stats.grade, fmt(stats.grade, oneDecimal), strings)}
            </p>
            <h3 className={WIDGET_H3}>{strings.jargonHeading}</h3>
            {jargon.length > 0 ? (
              <DataTable
                head={[
                  strings.jargonFound,
                  strings.jargonPlain,
                  strings.jargonCount,
                ]}
                rows={jargon.map((hit) => [
                  hit.found.join(strings.listSeparator),
                  hit.plain,
                  fmt(hit.count),
                ])}
              />
            ) : (
              <p className={HINT}>{strings.jargonNone}</p>
            )}
            {stats.longSentences.length > 0 ? (
              <>
                <h3 className={WIDGET_H3}>
                  {t(strings.longHeading, { count: fmt(LONG_SENTENCE_WORDS) })}
                </h3>
                <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
                  {stats.longSentences.map((sentence) => (
                    <li key={sentence.text}>
                      {tPlural(strings.longItem, locale, sentence.words, {
                        count: fmt(sentence.words),
                        text: sentence.text,
                      }).trim()}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        ) : (
          <Notice>{strings.prompt}</Notice>
        )}
      </section>
    </div>
  );
}
